import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'

import { useRef, useState } from 'react'
import Link from 'next/link'
import { ArrowRight, Check, ChevronDown, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button, buttonVariants } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { contactLimits, projectTypes } from '@/constants/contact'

type FieldName = 'name' | 'email' | 'message'
type Status = 'idle' | 'submitting' | 'success' | 'error'

interface FormValues {
    name: string
    email: string
    projectType: string
    message: string
    website: string
}

const EMPTY_VALUES: FormValues = {
    name: '',
    email: '',
    projectType: '',
    message: '',
    website: ''
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const validate = (values: FormValues): Partial<Record<FieldName, string>> => {
    const errors: Partial<Record<FieldName, string>> = {}

    if (!values.name.trim()) errors.name = "Please tell me your name."
    if (!EMAIL_PATTERN.test(values.email.trim())) errors.email = "Please enter a valid email address."
    if (values.message.trim().length < contactLimits.messageMin) errors.message = "A few words about your project would help."

    return errors
}

export const ContactForm: FC = (): ReactNode => {
    const [values, setValues] = useState<FormValues>(EMPTY_VALUES)
    const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({})
    const [status, setStatus] = useState<Status>('idle')
    const [serverError, setServerError] = useState<string | null>(null)
    const [sentName, setSentName] = useState('')
    const formRef = useRef<HTMLFormElement>(null)

    const update = (field: keyof FormValues) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { value } = event.target

        setValues(current => ({ ...current, [field]: value }))

        if (field in errors) setErrors(current => ({ ...current, [field]: undefined }))
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const nextErrors = validate(values)

        setErrors(nextErrors)
        setServerError(null)

        const firstInvalid = (['name', 'email', 'message'] as FieldName[]).find(field => nextErrors[field])

        if (firstInvalid) {
            formRef.current?.querySelector<HTMLElement>(`#contact-${firstInvalid}`)?.focus()
            return
        }

        setStatus('submitting')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            })

            const data = await response.json().catch(() => null) as { message?: string, errors?: Partial<Record<FieldName, string>> } | null

            if (!response.ok) {
                if (data?.errors) setErrors(data.errors)
                throw new Error(data?.message ?? "Your message could not be sent right now. Please try again in a moment.")
            }

            setSentName(values.name.trim().split(/\s+/)[0] ?? '')
            setValues(EMPTY_VALUES)
            setStatus('success')
        }
        catch (error) {
            setStatus('error')
            setServerError(error instanceof Error ? error.message : "Your message could not be sent right now. Please try again in a moment.")
        }
    }

    if (status === 'success') {
        return (
            <div role='status' className='flex h-full flex-col items-start justify-center rounded-3xl bg-card p-8 ring-1 ring-border sm:p-12'>
                <span className='inline-flex size-14 items-center justify-center rounded-full bg-accent text-accent-foreground'>
                    <Check aria-hidden='true' className='size-6' />
                </span>

                <h2 className='mt-8 font-heading text-4xl tracking-tight'>
                    Thank you{sentName ? `, ${sentName}` : ''}.
                </h2>

                <p className='mt-4 max-w-md text-lg leading-relaxed text-muted-foreground'>
                    Your message is on its way. I read every note myself and will reply by email.
                </p>

                <div className='mt-10 flex flex-wrap gap-3'>
                    <Link href='/journal' className={buttonVariants({ size: 'lg', className: 'group' })}>
                        Read the journal
                        <ArrowRight className='transition-transform group-hover:translate-x-0.5' />
                    </Link>

                    <Button variant='outline' size='lg' onClick={() => setStatus('idle')}>
                        Send another message
                    </Button>
                </div>
            </div>
        )
    }

    const fieldError = (field: FieldName) => errors[field] && (
        <p id={`contact-${field}-error`} className='text-sm text-destructive'>
            {errors[field]}
        </p>
    )

    return (
        <form
            ref={formRef}
            noValidate
            onSubmit={handleSubmit}
            className='relative rounded-3xl bg-card p-6 ring-1 ring-border sm:p-10'
        >
            <div className='grid gap-6 sm:grid-cols-2'>
                <div className='flex flex-col gap-2.5'>
                    <Label htmlFor='contact-name'>Your name</Label>

                    <Input
                        id='contact-name'
                        name='name'
                        autoComplete='name'
                        value={values.name}
                        maxLength={contactLimits.name}
                        onChange={update('name')}
                        aria-invalid={Boolean(errors.name)}
                        aria-describedby={errors.name ? 'contact-name-error' : undefined}
                    />

                    {fieldError('name')}
                </div>

                <div className='flex flex-col gap-2.5'>
                    <Label htmlFor='contact-email'>Email address</Label>

                    <Input
                        id='contact-email'
                        name='email'
                        type='email'
                        autoComplete='email'
                        inputMode='email'
                        value={values.email}
                        maxLength={contactLimits.email}
                        onChange={update('email')}
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={errors.email ? 'contact-email-error' : undefined}
                    />

                    {fieldError('email')}
                </div>

                <div className='flex flex-col gap-2.5 sm:col-span-2'>
                    <Label htmlFor='contact-project-type'>
                        What are you planning?
                        <span className='font-normal text-muted-foreground'>(optional)</span>
                    </Label>

                    <div className='relative'>
                        <select
                            id='contact-project-type'
                            name='projectType'
                            value={values.projectType}
                            onChange={update('projectType')}
                            className={cn(
                                'h-11 w-full appearance-none rounded-lg border border-input bg-card px-3.5 pr-10 text-base outline-none transition-colors',
                                'focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30',
                                !values.projectType && 'text-muted-foreground'
                            )}
                        >
                            <option value=''>Choose one</option>

                            {projectTypes.map(type => (
                                <option key={type} value={type}>
                                    {type}
                                </option>
                            ))}
                        </select>

                        <ChevronDown aria-hidden='true' className='pointer-events-none absolute top-1/2 right-3.5 size-4 -translate-y-1/2 text-muted-foreground' />
                    </div>
                </div>

                <div className='flex flex-col gap-2.5 sm:col-span-2'>
                    <Label htmlFor='contact-message'>Tell me about your space</Label>

                    <Textarea
                        id='contact-message'
                        name='message'
                        rows={6}
                        value={values.message}
                        maxLength={contactLimits.message}
                        onChange={update('message')}
                        placeholder="Which rooms, what you love, what isn't working, and any timing you have in mind."
                        aria-invalid={Boolean(errors.message)}
                        aria-describedby={errors.message ? 'contact-message-error' : undefined}
                    />

                    {fieldError('message')}
                </div>

                <div aria-hidden='true' className='absolute -left-[9999px] size-px overflow-hidden'>
                    <label htmlFor='contact-website'>Leave this field empty</label>

                    <input
                        id='contact-website'
                        name='website'
                        type='text'
                        tabIndex={-1}
                        autoComplete='off'
                        value={values.website}
                        onChange={update('website')}
                    />
                </div>
            </div>

            {serverError && (
                <p role='alert' className='mt-6 rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive'>
                    {serverError}
                </p>
            )}

            <div className='mt-8 flex flex-col-reverse gap-4 sm:flex-row sm:items-center sm:justify-between'>
                <p className='text-sm text-muted-foreground'>
                    Your details are only used to reply to you.
                </p>

                <Button type='submit' size='lg' disabled={status === 'submitting'} className='group'>
                    {status === 'submitting' ? (
                        <>
                            <LoaderCircle className='animate-spin' />
                            Sending
                        </>
                    ) : (
                        <>
                            Send message
                            <ArrowRight className='transition-transform group-hover:translate-x-0.5' />
                        </>
                    )}
                </Button>
            </div>
        </form>
    )
}
